import React, { useState, useContext, useEffect } from "react";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { Paper, Switch } from "@mui/material";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import UploadDocumentModal from "./uploadDocumentModal";
import "./documentDetailBox.scss";
import RejectedModal from "./rejectedModal";
import ReviewDocumentModel from "./reviewDocumentModal";
import RejectionReasonHistoryModal from "./RejectionReasonHistoryModal";
import { getUser } from "../../../config/user";
// import CountdownTimer from "../../../utils/countDownTimer";
import { CaseContext } from "../contexts/CaseContexts";
import { docUploadRefreshContext } from "../contexts/CaseContexts";
import ImagesGalleryHospitalPersona from "../../../hospitalPersona/imageGallery/ImageGalleryHospitalPersona";
import { CaseServices } from "../../../services/CaseServices";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";

const DocumentDetailBox = ({ docDetails, onDelete, siblingDocuments }) => {
  let [documentDetails, setDocumentDetails] = useState(docDetails);
  const [isGalleryOpen, setGalleryOpen] = useState(-1);
  const [isRejectionHistoryOpen, setRejectionHistoryOpen] = useState(false);
  const [hasDeletedRejectionHistory, setHasDeletedRejectionHistory] =
    useState(false);
  const { roleName } = useContext(CaseContext);
  const user = getUser();
  const userRoles = user?.roles;
  const refreshFunction = useContext(docUploadRefreshContext);

  const hasRejectionReason = (history) => {
    return (
      Array.isArray(history?.rejectionReason) &&
      history.rejectionReason.some((item) => item?.rejectionReason?.trim())
    );
  };

  const hasAnyRejectionReason = () => {
    const hasWholeDocumentReason =
      documentDetails?.rejectionReason?.trim() ||
      hasRejectionReason(documentDetails?.history);

    const hasIndividualReason = documentDetails?.documenturlmasters?.some(
      (document) =>
        document?.deleted !== true &&
        (document?.rejectionReason?.trim() ||
          hasRejectionReason(document?.history)),
    );

    return Boolean(
      hasWholeDocumentReason ||
      hasIndividualReason ||
      hasDeletedRejectionHistory,
    );
  };

  const statusBtnStyle = (() => {
    if (documentDetails?.status === "REJECTED") {
      return { color: "#CF3636", backgroundColor: "#FFD6DB" };
    } else if (documentDetails?.status === "INREVIEW") {
      return { color: "#017C57", backgroundColor: "#CDFAF4" };
    } else if (documentDetails?.status === "APPROVED") {
      return { color: "white", backgroundColor: "#017C57" };
    } else {
      return {};
    }
  })();

  const checkDeletedRejectionHistory = async () => {
    if (!documentDetails?.id) return;

    try {
      const res = await CaseServices?.getDeletedDocUrls(documentDetails.id);

      const deletedDocuments = res?.data?.doc?.documenturlmasters || [];

      const hasDeletedReason = deletedDocuments.some(
        (document) =>
          document?.rejectionReason?.trim() ||
          hasRejectionReason(document?.history),
      );

      setHasDeletedRejectionHistory(hasDeletedReason);
    } catch (error) {
      setHasDeletedRejectionHistory(false);
    }
  };

  const getDocumentsData = async () => {
    await refreshFunction.getMappedDocuments();
  };

  // Check if delete button should be visible
  const shouldShowDeleteButton = () => {
    // Show delete button if:
    // 1. Status is PENDING
    // 2. NOT Admission or Discharge document
    return (
      documentDetails.status === "PENDING" &&
      documentDetails.documentType !== "ADMISSIONDOCUMENT" &&
      documentDetails.documentType !== "ONDISCHARGEDOCUMENT"
    );
  };

  const handleUpdateDocRequiredStatus = async (doc) => {
    try {
      await CaseServices.updateDocRequiredStatus(doc.id, !doc?.required);
      await getDocumentsData();
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };
  const handleDelete = async () => {
    // Count PENDING documents
    const pendingCount =
      siblingDocuments?.filter((doc) => doc.status === "PENDING").length || 0;

    // Count total documents (all statuses)
    const totalDocuments = siblingDocuments?.length || 0;

    // Block deletion ONLY if:
    // 1. This is the last PENDING document (pendingCount <= 1)
    // 2. AND there are no other documents with different statuses (totalDocuments === pendingCount)
    if (pendingCount <= 1 && totalDocuments === pendingCount) {
      window.alert(
        "Cannot delete the last document of this type. At least one document must remain.",
      );
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${documentDetails?.documentName}"?`,
    );

    if (!confirmDelete) return;

    try {
      if (documentDetails?.caseId && documentDetails?.id) {
        await CaseServices.deleteDocument(documentDetails.id);
        await refreshFunction.getMappedDocuments();
        window.alert("Document deleted successfully");
      } else if (onDelete) {
        onDelete(documentDetails);
      }
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };

  useEffect(() => {
    checkDeletedRejectionHistory();
  }, [documentDetails?.id]);

  // Keep the card in sync when the parent refreshes data in the background
  // (e.g. a document approved/rejected from the review modal).
  useEffect(() => {
    setDocumentDetails(docDetails);
  }, [docDetails]);

  return (
    <Paper className="document-detail-box relative">
      {documentDetails.status !== "PENDING" && (
        <div className="absolute top-1 right-1 flex items-center gap-2">
          {hasAnyRejectionReason() && (
            <div
              className="cursor-pointer"
              title="View Rejection Reasons"
              onClick={() => setRejectionHistoryOpen(true)}
            >
              <ErrorOutlineOutlinedIcon
                sx={{
                  color: "#CF3636",
                  fontSize: 22,
                }}
              />
            </div>
          )}

          <div className="cursor-pointer" onClick={() => setGalleryOpen(true)}>
            <div className="relative">
              <ChatBubbleOutlineOutlinedIcon />

              {documentDetails?.totalUnreadComments > 0 && (
                <div className="absolute -top-2 -right-1 w-[20px] h-[20px] flex justify-center items-center text-[10px] text-white bg-[#ff0000] rounded-full">
                  {documentDetails?.totalUnreadComments > 99
                    ? "99+"
                    : documentDetails?.totalUnreadComments}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {shouldShowDeleteButton() && (
        <div className="absolute top-1 right-1 flex items-center gap-1 cursor-pointer">
          {/* {!documentDetails?.required && (
            <DeleteOutlinedIcon
              sx={{ color: "#FE6234", fontSize: 24 }}
              onClick={handleDelete}
            />
          )} */}
          <DeleteOutlinedIcon
            sx={{ color: "#FE6234", fontSize: 24 }}
            onClick={handleDelete}
          />
          {/* <div>
            <Switch
              checked={documentDetails?.required ? true : false}
              onChange={() => {
                if (
                  window.confirm(
                    "Are you sure you want to toggle the document status",
                  )
                ) {
                  handleUpdateDocRequiredStatus(documentDetails);
                }
              }}
              color="success"
              // size="small"
            />
          </div> */}
        </div>
      )}

      <div className="model-heading">
        <div className="left-container">
          <div style={{ width: "10px", color: "#017C57" }}>
            <DescriptionOutlinedIcon />
          </div>
          <div className="w-[80%]">{documentDetails?.documentName}</div>
        </div>
        <button className="contained-btn" style={statusBtnStyle}>
          {documentDetails?.status}
        </button>
      </div>
      {documentDetails?.status !== "REJECTED" ? (
        <div className="footer">
          {documentDetails?.documenturlmasters?.length > 0 ? (
            <>
              <div className="uploaded-btn">
                Uploaded{" "}
                <CheckCircleOutlineOutlinedIcon sx={{ height: "16px" }} />
              </div>
              {documentDetails.status === "INREVIEW" ? (
                userRoles?.filter((role) =>
                  role?.role?.roleName?.toLowerCase().includes("doctor"),
                )?.length > 0 &&
                (refreshFunction?.openReview ? (
                  // Review is hosted by the parent page so the user can move
                  // between documents (Next) without the modal being unmounted
                  <button
                    type="button"
                    className="upload-btn"
                    onClick={() =>
                      refreshFunction.openReview(
                        documentDetails,
                        siblingDocuments,
                      )
                    }
                  >
                    REVIEW
                  </button>
                ) : (
                  <ReviewDocumentModel
                    documentDetails={documentDetails}
                    setDocumentDetails={setDocumentDetails}
                    type="review"
                  />
                ))
              ) : (
                <ReviewDocumentModel
                  documentDetails={documentDetails}
                  setDocumentDetails={setDocumentDetails}
                  type="view"
                />
              )}
            </>
          ) : (
            <>
              <UploadDocumentModal
                documentDetails={documentDetails}
                setDocumentDetails={setDocumentDetails}
              />
            </>
          )}
        </div>
      ) : (
        <div className="footer-rejected">
          <RejectedModal documentDetails={documentDetails} />
          <ReviewDocumentModel
            documentDetails={documentDetails}
            setDocumentDetails={setDocumentDetails}
            type="view"
          />
        </div>
      )}
      {isGalleryOpen >= 0 && (
        <ImagesGalleryHospitalPersona
          data={documentDetails?.documenturlmasters}
          setOpen={setGalleryOpen}
          view={true}
          type={"document"}
          getDocumentsData={getDocumentsData}
          documentId={documentDetails?.id}
        />
      )}

      {isRejectionHistoryOpen && (
        <RejectionReasonHistoryModal
          documentDetails={documentDetails}
          open={isRejectionHistoryOpen}
          setOpen={setRejectionHistoryOpen}
        />
      )}
    </Paper>
  );
};

export default DocumentDetailBox;
